/**
 * Copyright (c) 2019 Vendor Badge, LLC All Rights Reserved.
 * Created Date: Sunday, 17th February 2019
 *
 *  Portal - Login View/Registration Wizard/User Profile
 */
import React from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { withStyles } from '@material-ui/core/styles';
import { observer } from 'mobx-react';
import { useStores } from 'stores/RootStore';
import AddressForm from 'components/Forms/AddressForm';
import TextFieldWithValidation from 'components/InputFields/TextFieldWithValidation';

// Material UI Components
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import InputAdornment from '@material-ui/core/InputAdornment';
import EmailIcon from '@material-ui/icons/Email';
import PhoneIcon from '@material-ui/icons/PhoneAndroid';
import CircularProgress from '@material-ui/core/CircularProgress';
import AvatarInput from 'components/InputFields/Avatar';

const UserProfile = props => {
  const { classes } = props;
  const stores = useStores();

  const {
    invite,
    inviteId,
    validInvite,
    inviteCheckCount,
    isLoading,
    type,
    userFirstName,
    userLastName,
    userPhone,
    userEmail,
    userBio,
    userAvatarUrl,
    handleUserInputUpdate,
    handleAvatarChange,
    getInviteDetails,
  } = stores.registrationStore;

  // Check for registration type
  if (type !== '') {
    // Check for invitation
    if (invite && inviteCheckCount === 0) {
      // Check to see if we have a valid invite id in query params
      if (inviteId !== null) {
        stores.appStore.debug(`Login::UserProfile Param id found ${inviteId}, checking for validity...`);
        getInviteDetails(inviteId).catch(error => {
          // User invite not found or invalid.
          stores.appStore.debug('Login::UserProfile Invalid user invite.', error);
        });
      } else {
        // Missing invite id, invalid invite
        stores.appStore.debug(`Login::UserProfile Invalid invite id or missing invite id.`);
      }
    }
  } else {
    // No query params found, push to choose
    stores.appStore.info('No query params found, sending user to registration choice view.');
    props.history.push('/login/choose');
  }

  if (isLoading) {
    // Loader
    stores.appStore.debug('Login-RegistrationWizard-UserProfile [component] Loading...');
    return (
      <Grid container direction='column' justify='center' alignItems='center' style={{ height: '300px' }}>
        <Grid item>
          <CircularProgress size={68} />
        </Grid>
      </Grid>
    );
  } else if (invite && !validInvite) {
    // Not a valid invite
    stores.appStore.debug('Login-RegistrationWizard-UserProfile [component] Not a valid invite.');
    return (
      <Grid container justify='center' alignItems='center' style={{ height: '300px', textAlign: 'center' }}>
        <Grid item>
          <Typography color='primary'>
            Not a valid invite. Please reach out to your portal administrator to resend your invite.
          </Typography>
        </Grid>
      </Grid>
    );
  } else if ((!invite && !isLoading) || (invite && validInvite)) {
    // Not an invite or is a valid invite, return normal registration page
    return (
      <form id='user-profile' autoComplete='off'>
        <Grid container justify='center' className={classes.gridContainer}>
          <Grid item sm={6} xs={12} className={classes.addressGridItem}>
            <Grid container>
              <Typography variant='body1' color='textSecondary' className={classes.sectionTitle}>
                What should we call you?
              </Typography>
              <Divider className={classes.sectionDivider} />
              <Grid item sm={5} xs={12}>
                <TextFieldWithValidation
                  id='first-name'
                  name='FirstName'
                  type='text'
                  required
                  visible
                  message='First name is required.'
                  value={userFirstName}
                  handleInputChange={handleUserInputUpdate}
                  label='First Name'
                  className={classes.firstName}
                  fullWidth
                  margin='dense'
                  InputProps={{
                    className: classes.inputText,
                  }}
                  InputLabelProps={{
                    className: classes.inputLabelText,
                  }}
                />
              </Grid>
              <Grid item sm={7} xs={12}>
                <TextFieldWithValidation
                  id='last-name'
                  name='LastName'
                  type='text'
                  required
                  visible
                  message='Last name is required.'
                  label='Last Name'
                  value={userLastName}
                  handleInputChange={handleUserInputUpdate}
                  className={classes.lastName}
                  fullWidth
                  margin='dense'
                  InputProps={{
                    className: classes.inputText,
                  }}
                  InputLabelProps={{
                    className: classes.inputLabelText,
                  }}
                />
              </Grid>
              <Typography variant='body1' color='textSecondary' className={classes.sectionTitle}>
                How do we reach you?
              </Typography>
              <Divider className={classes.sectionDivider} />
              <Grid item sm={5} xs={12}>
                <TextFieldWithValidation
                  id='phone-number'
                  name='Phone'
                  type='text'
                  required
                  visible
                  pattern='^1?[-\. ]?(\(\d{3}\)?[-\. ]?|\d{3}?[-\. ]?)?\d{3}?[-\. ]?\d{4}$'
                  message='A valid U.S. phone number is required.'
                  label='Phone'
                  value={userPhone}
                  handleInputChange={handleUserInputUpdate}
                  className={classes.phoneNumber}
                  fullWidth
                  margin='dense'
                  InputLabelProps={{
                    className: classes.inputLabelText,
                  }}
                  InputProps={{
                    className: classes.inputText,
                    endAdornment: (
                      <InputAdornment>
                        <PhoneIcon className={classes.inputIcon} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item sm={7} xs={12}>
                <TextFieldWithValidation
                  id='email-address'
                  name='Email'
                  type='email'
                  required
                  visible
                  message='A valid email address is required.'
                  label='Email'
                  value={userEmail}
                  handleInputChange={handleUserInputUpdate}
                  className={classes.emailAddress}
                  fullWidth
                  margin='dense'
                  InputLabelProps={{
                    className: classes.inputLabelText,
                  }}
                  InputProps={{
                    className: classes.inputText,
                    endAdornment: (
                      <InputAdornment>
                        <EmailIcon className={classes.inputIcon} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Typography
                variant='body1'
                color='textSecondary'
                className={classes.sectionTitle}
                style={{ paddingTop: '11px' }}
              >
                How do we identify you?
              </Typography>
              <Divider className={classes.sectionDivider} />
            </Grid>
            <Grid container justify='center' direction='column' alignItems='center' className={classes.avatarGrid}>
              <Grid item xs={12} className={classes.avatarInput}>
                <AvatarInput required url={userAvatarUrl} handleAvatarChange={handleAvatarChange} />
                <Typography variant='h6' color='textSecondary' className={classes.avatarFooter}>
                  Choose Picture{type === 'vendor' ? '*' : ''}
                </Typography>
                <Typography variant='caption' color='textSecondary'>
                  {'( gif, jpg, png )'}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item sm={6} xs={12}>
            {type === 'vendor' ? (
              <Grid item xs={12}>
                <Typography variant='body1' color='textSecondary' className={classes.sectionTitle}>
                  Where do you live?
                </Typography>
                <Divider className={classes.sectionDivider} />
                <AddressForm
                  address1
                  address2
                  city
                  state
                  zip
                  handleInputUpdate={handleUserInputUpdate}
                  toStore={true}
                  store={stores.registrationStore}
                  prefix={'user'}
                />
              </Grid>
            ) : null}
            <Typography variant='body1' color='textSecondary' className={classes.sectionTitle}>
              Tell us your story...
            </Typography>
            <Divider className={classes.sectionDivider} />
            <TextFieldWithValidation
              id='user-bio'
              name='Bio'
              helperText='Visible to all VendorBadge users.'
              multiline
              rows='8'
              visible
              value={userBio}
              handleInputChange={handleUserInputUpdate}
              fullWidth
              margin='dense'
              InputLabelProps={{
                className: classes.inputLabelText,
              }}
              InputProps={{
                className: classes.inputTextBio,
              }}
            />
          </Grid>
        </Grid>
      </form>
    );
  }
};

UserProfile.propTypes = {
  classes: PropTypes.object.isRequired,
};

const styles = theme => ({
  gridContainer: {},
  addressGridItem: {
    paddingLeft: '5px',
    paddingRight: '10px',
    paddingBottom: '5px',
    [theme.breakpoints.down('xs')]: {
      paddingRight: '15px',
      paddingLeft: '15px',
    },
  },
  inputIcon: {
    color: theme.palette.type === 'light' ? 'rgb(128, 128, 128)' : theme.palette.common.white,
    padding: '0',
  },
  avatarGrid: {
    paddingLeft: '10px',
    paddingRight: '5px',
    paddingBottom: '5px',
    [theme.breakpoints.down('xs')]: {
      paddingRight: '15px',
      paddingLeft: '15px',
    },
    textAlign: 'center',
  },
  avatarInput: {
    paddingTop: '3px',
  },
  avatarFooter: {
    fontSize: '10pt',
    textAlign: 'center',
  },
  sectionTitleGrid: {
    marginBottom: '10px',
  },
  sectionTitle: {
    marginLeft: '-5px',
    marginTop: '15px',
    fontWeight: 600,
    color: theme.palette.primary.light,
  },
  sectionDivider: {
    width: '100%',
    marginLeft: '-5px',
    marginBottom: '10px',
  },
  firstName: {
    padding: '2px',
    paddingRight: '10px',
    [theme.breakpoints.down('xs')]: {
      padding: '2px',
    },
  },
  lastName: {
    padding: '2px',
    [theme.breakpoints.down('xs')]: {
      padding: '2px',
    },
  },
  phoneNumber: {
    padding: '2px',
    paddingRight: '10px',
    [theme.breakpoints.down('xs')]: {
      padding: '2px',
    },
  },
  emailAddress: {
    padding: '2px',
    [theme.breakpoints.down('xs')]: {
      padding: '2px',
    },
  },
  inputLabelText: {
    fontSize: '11pt',
    [theme.breakpoints.down('xs')]: {
      fontSize: '10pt',
    },
  },
  inputTextBio: {
    fontSize: '10pt',
  },
});

export default withRouter(withStyles(styles, { withTheme: true })(observer(UserProfile)));
